import { buildActivityLogDetailRows } from "./activityLogDetail";

export const MRC_ACTIVITY_STEPS = [
  {
    id: 1,
    label: "Application Submitted",
    slaDays: 1,
    aliases: ["Documents Uploading", "Application Submission"],
  },
  {
    id: 2,
    label: "Document Verification",
    slaDays: 1,
    aliases: ["Documents Verification", "Document Verified"],
  },
  {
    id: 3,
    label: "Authority Verification",
    slaDays: 1,
    aliases: ["Authority Verified", "Approval / Rejection"],
  },
  {
    id: 4,
    label: "Site Survey",
    slaDays: 1,
    aliases: ["Field Survey", "Site Survey Completed"],
  },
  {
    id: 5,
    label: "Forward for Billing",
    slaDays: 1,
    aliases: ["Forward To Billing", "Forward to Billing"],
  },
  {
    id: 6,
    label: "Post for Billing",
    slaDays: 1,
    aliases: ["Post to Billing", "Posted to Billing"],
  },
  {
    id: 7,
    label: "Completed",
    slaDays: 1,
    aliases: ["Application Completed"],
  },
];

/** History rows store the completed step in historyProgressId; applicationProgressId is the next step. */
export function getMrcHistoryStepProgressId(item) {
  if (!item || typeof item !== "object") return null;
  if (item.historyProgressId != null && item.historyProgressId !== "") {
    const parsed = Number(item.historyProgressId);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (item.applicationProgressId != null && item.applicationProgressId !== "") {
    const parsed = Number(item.applicationProgressId);
    if (Number.isFinite(parsed)) return Math.max(0, parsed - 1);
  }
  return null;
}

export function isMrcDocumentsReuploadedHistory(item) {
  if (!item || typeof item !== "object") return false;
  const remarks = String(item.remarks || item.remarksDetail || "").trim();
  return /re-?uploaded/i.test(remarks);
}

export function getMrcHistoryStepName(item) {
  if (!item || typeof item !== "object") return "";
  if (isMrcDocumentsReuploadedHistory(item)) return "Documents Reupload";
  const fromFields =
    item.mRCProgStatusName ||
    item.mrcProgStatusName ||
    item.cOMRProgStatusName ||
    item.comrProgStatusName ||
    item.progStatusName ||
    item.stepName ||
    "";
  if (fromFields && !/re-?uploaded/i.test(fromFields)) return fromFields;
  if (fromFields) return "Documents Reupload";

  const progressId = getMrcHistoryStepProgressId(item);
  if (progressId == null) return "";
  const step = MRC_ACTIVITY_STEPS.find((def) => def.id === progressId + 1);
  return step?.label || "";
}

export function normalizeMrcHistoryItem(item) {
  if (!item || typeof item !== "object") return item;
  const stepLabel = getMrcHistoryStepName(item);
  return {
    ...item,
    mRCProgStatusName: stepLabel || item.mRCProgStatusName,
    mrcProgStatusName: stepLabel,
  };
}

/**
 * History popup reading table: show everything added up to that step (read-only).
 * historyProgressId: 0 Submitted, 1 Doc Verify, 2 Authority, 3 Site Survey,
 * 4 Forward, 5 Post, 6 Completed.
 */
export function getMrcHistoryReadingTableFlags(
  historyItem = {},
  { siteSurveyHappened = false } = {}
) {
  const stepId = getMrcHistoryStepProgressId(historyItem);
  const id = Number.isFinite(Number(stepId)) ? Number(stepId) : -1;

  // Re-upload / Submitted / Doc Verification → claimed + evidence only
  if (isMrcDocumentsReuploadedHistory(historyItem) || id <= 1) {
    return { showAuthorityClaimed: false, showSiteSurvey: false };
  }

  // Authority Verification → claimed + authority approved
  if (id === 2) {
    return { showAuthorityClaimed: true, showSiteSurvey: false };
  }

  // Site Survey step → claimed + authority + site survey (all added so far)
  if (id === 3) {
    return { showAuthorityClaimed: true, showSiteSurvey: true };
  }

  // Forward / Post / Completed → full approved table
  return {
    showAuthorityClaimed: true,
    showSiteSurvey: Boolean(siteSurveyHappened),
  };
}

/** Latest Site Survey history row at or before this historyId. */
export function findPriorMrcSiteSurveyHistory(historyRows = [], historyId) {
  const currentId = Number(historyId);
  const rows = Array.isArray(historyRows) ? historyRows : [];
  return (
    rows
      .filter((row) => {
        if (!row || isMrcDocumentsReuploadedHistory(row)) return false;
        if (getMrcHistoryStepProgressId(row) !== 3) return false;
        if (!Number.isFinite(currentId)) return true;
        const rowId = Number(row.historyId);
        return !Number.isFinite(rowId) || rowId <= currentId;
      })
      .sort((a, b) => Number(b.historyId || 0) - Number(a.historyId || 0))[0] ||
    null
  );
}

/** Whether a Site Survey step had already been completed by this history row. */
export function didMrcSiteSurveyHappenAtHistory(historyRows = [], historyId) {
  return Boolean(findPriorMrcSiteSurveyHistory(historyRows, historyId));
}

export function buildMrcActivityLogDetailRows(
  historyItems = [],
  currentStatus = {},
  formatDateFn,
) {
  return buildActivityLogDetailRows(historyItems, currentStatus, formatDateFn, {
    steps: MRC_ACTIVITY_STEPS,
    stepNameField: "mrcProgStatusName",
  });
}
